import { VENDOR_CATEGORIES, VENDOR_STATUSES } from './constants.js';
import type { VendorCategory, VendorStatus } from './constants.js';
import type { Payment, Vendor } from './schemas/vendor.js';

/**
 * Vendor and payment arithmetic, pure and shared by the API and the web client
 * so the tab and the summary endpoint can never disagree about what is owed.
 *
 * The number that matters on this tab is **what is still to pay**, not what
 * things cost. A wedding is a series of deposits and balances falling due on
 * dates somebody has to remember, and the quote stops being the interesting
 * figure the moment the first deposit goes out.
 */

/** Paid is a date, not a flag: a payment with no `paidAt` has not happened. */
export const isPaid = (payment: Payment): boolean => Boolean(payment.paidAt);

export function paidCents(vendor: Vendor): number {
  return (vendor.payments ?? []).reduce((sum, p) => (isPaid(p) ? sum + p.amountCents : sum), 0);
}

/** What the payment schedule accounts for, paid or not. */
export function scheduledCents(vendor: Vendor): number {
  return (vendor.payments ?? []).reduce((sum, p) => sum + p.amountCents, 0);
}

/**
 * Still to pay.
 *
 * Measured against the quote, because that is the commitment. A schedule that
 * does not add up to the quote is normal — the last instalment often has no date
 * yet — so the balance is deliberately not "the sum of unpaid instalments".
 *
 * `undefined` rather than 0 when there is no quote: a vendor nobody has priced
 * owes an unknown amount, which is not the same as owing nothing.
 */
export function balanceCents(vendor: Vendor): number | undefined {
  if (vendor.quoteCents === undefined) return undefined;
  return Math.max(0, vendor.quoteCents - paidCents(vendor));
}

/**
 * Money on the schedule that has no date against it.
 *
 * Worth surfacing separately: an instalment with no due date will never appear
 * in "what is coming up", so without this it is invisible until someone chases.
 */
export function undatedCents(vendor: Vendor): number {
  return (vendor.payments ?? []).reduce(
    (sum, p) => (!isPaid(p) && !p.dueDate ? sum + p.amountCents : sum),
    0,
  );
}

export interface DuePayment {
  vendorId: string;
  vendorName: string;
  payment: Payment;
  currency: string;
  /** Days from today. Negative is overdue. */
  inDays: number;
  overdue: boolean;
}

const DAY = 24 * 60 * 60 * 1000;

/** Midnight comparison, so "due today" is not overdue by a few hours. */
const daysBetween = (isoDate: string, today: Date): number | undefined => {
  const due = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(due.getTime())) return undefined;
  const from = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.round((due.getTime() - from) / DAY);
};

/**
 * Everything unpaid with a date, soonest first, overdue included.
 *
 * Overdue is listed rather than hidden or held separately: the point of the list
 * is what needs doing, and something you have already missed needs doing most.
 */
export function upcomingPayments(
  vendors: Vendor[],
  options: { today?: Date; withinDays?: number } = {},
): DuePayment[] {
  const today = options.today ?? new Date();
  const out: DuePayment[] = [];

  for (const vendor of vendors) {
    for (const payment of vendor.payments ?? []) {
      if (isPaid(payment) || !payment.dueDate) continue;
      const inDays = daysBetween(payment.dueDate, today);
      if (inDays === undefined) continue;
      if (options.withinDays !== undefined && inDays > options.withinDays) continue;
      out.push({
        vendorId: vendor.vendorId,
        vendorName: vendor.name,
        payment,
        currency: vendor.currency,
        inDays,
        overdue: inDays < 0,
      });
    }
  }

  return out.sort((a, b) => a.inDays - b.inDays || a.vendorName.localeCompare(b.vendorName));
}

/** The next thing owed to one vendor, or nothing. */
export function nextDue(vendor: Vendor, today = new Date()): DuePayment | undefined {
  return upcomingPayments([vendor], { today })[0];
}

export interface CategorySpend {
  category: VendorCategory;
  vendors: number;
  quotedCents: number;
  paidCents: number;
  /** Quoted minus paid across the category, floored at zero. */
  outstandingCents: number;
}

/**
 * What each kind of supplier costs, for the bar on the tab.
 *
 * Only categories with a vendor in them, in catalog order — an empty row per
 * unused category would be twenty-two rows of nothing.
 */
export function spendByCategory(vendors: Vendor[]): CategorySpend[] {
  const byCategory = new Map<VendorCategory, CategorySpend>();

  for (const vendor of vendors) {
    const category = vendor.category ?? 'other';
    const row = byCategory.get(category) ?? {
      category,
      vendors: 0,
      quotedCents: 0,
      paidCents: 0,
      outstandingCents: 0,
    };
    row.vendors += 1;
    row.quotedCents += vendor.quoteCents ?? 0;
    row.paidCents += paidCents(vendor);
    byCategory.set(category, row);
  }

  return VENDOR_CATEGORIES.filter((c) => byCategory.has(c)).map((c) => {
    const row = byCategory.get(c) as CategorySpend;
    return { ...row, outstandingCents: Math.max(0, row.quotedCents - row.paidCents) };
  });
}

export interface VendorStats {
  total: number;
  byStatus: Record<VendorStatus, number>;
  booked: number;
  quotedCents: number;
  paidCents: number;
  outstandingCents: number;
  /** Unpaid, dated, and already past. The number worth a red pill. */
  overdue: number;
  /** Unpaid instalments with no date at all. */
  undatedCents: number;
  /**
   * Every quote on the board: booked, paid **and** merely quoted.
   *
   * Deliberately a second number rather than a widening of `quotedCents`, which
   * counts only what has been committed to and is what the budget's "committed"
   * column means. Both are true and they answer different questions — this one
   * is "what would the suppliers cost if we went with everyone we have a price
   * from", which is the figure you want while still choosing.
   *
   * Someone still being researched contributes nothing even if a price was typed
   * in: without a quote there is nothing to add, and with one they are quoted.
   */
  expectedCents: number;
  /** How many suppliers that figure is made of, so it can be read honestly. */
  expectedFrom: number;
  currency: string;
}

export function computeVendorStats(vendors: Vendor[], today = new Date()): VendorStats {
  const byStatus = Object.fromEntries(VENDOR_STATUSES.map((s) => [s, 0])) as Record<
    VendorStatus,
    number
  >;
  for (const vendor of vendors) byStatus[vendor.status ?? 'researching'] += 1;

  // Only what has been committed to. A quote from someone you have not booked is
  // a price you were told, not money you owe.
  const committed = vendors.filter((v) => v.status === 'booked' || v.status === 'paid');
  const quoted = committed.reduce((sum, v) => sum + (v.quoteCents ?? 0), 0);
  const paid = vendors.reduce((sum, v) => sum + paidCents(v), 0);

  // Booked, paid or quoted — anyone who has given a number. A supplier still
  // being researched has no price to add.
  const priced = vendors.filter(
    (v) =>
      v.quoteCents !== undefined &&
      (v.status === 'booked' || v.status === 'paid' || v.status === 'quoted'),
  );

  return {
    total: vendors.length,
    byStatus,
    booked: byStatus.booked + byStatus.paid,
    quotedCents: quoted,
    paidCents: paid,
    outstandingCents: Math.max(0, quoted - paid),
    overdue: upcomingPayments(vendors, { today }).filter((p) => p.overdue).length,
    undatedCents: vendors.reduce((sum, v) => sum + undatedCents(v), 0),
    expectedCents: priced.reduce((sum, v) => sum + (v.quoteCents ?? 0), 0),
    expectedFrom: priced.length,
    currency: vendors[0]?.currency ?? 'USD',
  };
}

export type VendorSort = 'name' | 'category' | 'quote' | 'due' | 'status';

/**
 * Sorts the table. Vendors missing the value being sorted on sink to the bottom
 * rather than being treated as zero, which would put an unpriced supplier above
 * a cheap one — the same rule the venue list follows.
 */
export function sortVendors(vendors: Vendor[], sort: VendorSort, today = new Date()): Vendor[] {
  return [...vendors].sort((a, b) => {
    switch (sort) {
      case 'quote': {
        if (a.quoteCents === undefined && b.quoteCents === undefined)
          return a.name.localeCompare(b.name);
        if (a.quoteCents === undefined) return 1;
        if (b.quoteCents === undefined) return -1;
        return b.quoteCents - a.quoteCents;
      }
      case 'due': {
        const da = nextDue(a, today);
        const db = nextDue(b, today);
        if (!da && !db) return a.name.localeCompare(b.name);
        if (!da) return 1;
        if (!db) return -1;
        return da.inDays - db.inDays;
      }
      case 'status': {
        const order = (v: Vendor) => VENDOR_STATUSES.indexOf(v.status ?? 'researching');
        return order(a) - order(b) || a.name.localeCompare(b.name);
      }
      case 'category':
        return (
          VENDOR_CATEGORIES.indexOf(a.category ?? 'other') -
            VENDOR_CATEGORIES.indexOf(b.category ?? 'other') || a.name.localeCompare(b.name)
        );
      default:
        return a.name.localeCompare(b.name);
    }
  });
}

/** Groups the table the way the tab shows it: by category, in catalog order. */
export function groupByCategory(
  vendors: Vendor[],
): { category: VendorCategory; vendors: Vendor[] }[] {
  const grouped = new Map<VendorCategory, Vendor[]>();
  for (const vendor of vendors) {
    const category = vendor.category ?? 'other';
    grouped.set(category, [...(grouped.get(category) ?? []), vendor]);
  }
  return VENDOR_CATEGORIES.filter((c) => grouped.has(c)).map((category) => ({
    category,
    vendors: grouped.get(category) ?? [],
  }));
}
